import { useMemo } from 'react'
import { CheckBox } from 'grommet'
import useFulfillmentQueue, { FulfillmentQueueName } from 'hooks/useFulfillmentQueue'
import type { Selectable } from 'providers/SelectionProvider'
import { useSelectionContext } from 'providers/SelectionProvider'
import { usePlusClient } from 'providers/VisionRouter'
import type { Order, Fill } from 'types'

type SelectItem = { _id: string; fill?: Fill; order: Order } | null
type QueueSelectedCheckboxProps = { items: Order[]; isBulkActionQueue?: boolean; disabled?: boolean }

const QueueSelectedCheckbox = ({
  items,
  isBulkActionQueue = false,
  disabled = false,
}: QueueSelectedCheckboxProps): JSX.Element => {
  const fulfillmentQueue = useFulfillmentQueue()
  const { bulkSetSelections, removeBulkSelections, totalSelectableItems, selections } = useSelectionContext()
  const {
    tokenContext: { isLeadPharmacist, isPharmacist, isTechnician },
  } = usePlusClient()

  const isOrderLevelSelection =
    (isBulkActionQueue && fulfillmentQueue?.name !== FulfillmentQueueName.Fill) ||
    fulfillmentQueue?.name === FulfillmentQueueName.Packing

  const { isChecked, isSomeChecked } = useMemo(() => {
    if (isOrderLevelSelection) {
      const allSelected =
        items.length > 0 && items.every(item => selections.some(s => (s.order?._id ?? s._id) === item._id))
      const someSelected =
        !allSelected && items.some(item => selections.some(s => (s.order?._id ?? s._id) === item._id))
      return { isChecked: allSelected, isSomeChecked: someSelected }
    } else {
      const pageSelectionCount = selections.filter(s =>
        items.some(item => item.rxFillRequests.some(rxf => s._id === rxf.fillId)),
      ).length
      const allSelected = pageSelectionCount > 0 && pageSelectionCount === totalSelectableItems
      const someSelected = !allSelected && pageSelectionCount > 0
      return { isChecked: allSelected, isSomeChecked: someSelected }
    }
  }, [totalSelectableItems, items, isOrderLevelSelection, selections])

  if (
    fulfillmentQueue?.name === FulfillmentQueueName.Fill ||
    fulfillmentQueue?.name === FulfillmentQueueName.Automation ||
    fulfillmentQueue?.name === FulfillmentQueueName.Packing ||
    isBulkActionQueue
  ) {
    return (
      <CheckBox
        checked={isChecked}
        indeterminate={isSomeChecked}
        disabled={disabled}
        onChange={event => {
          if (isSomeChecked || !event.target.checked) {
            // Indeterminate or unchecking: clear current page selections
            const deselectMe = items
              .flatMap<SelectItem>(item => {
                if (
                  fulfillmentQueue?.name === FulfillmentQueueName.Fill ||
                  fulfillmentQueue?.name === FulfillmentQueueName.Automation
                ) {
                  return item.rxFillRequests.map(rxf => ({ _id: rxf.fillId, fill: rxf.fill, order: item }))
                } else if (isBulkActionQueue || fulfillmentQueue?.name === FulfillmentQueueName.Packing) {
                  return [{ _id: item._id, order: item }]
                } else return null
              })
              .filter(n => !!n)
            removeBulkSelections(deselectMe as Selectable[])
            return
          }
          if (event.target.checked) {
            const selectMe = items
              .flatMap<SelectItem>(item => {
                if (fulfillmentQueue?.name === FulfillmentQueueName.Fill) {
                  return item.rxFillRequests.map(rxf => {
                    if ((rxf.fill?.labelsPrinted || 0) > 0 && !isLeadPharmacist() && !isPharmacist() && !isTechnician)
                      return null

                    return { _id: rxf.fillId, fill: rxf.fill, order: item }
                  })
                } else if (fulfillmentQueue?.name === FulfillmentQueueName.Automation) {
                  return item.rxFillRequests.map(rxf => ({ _id: rxf.fillId, fill: rxf.fill, order: item }))
                } else if (isBulkActionQueue || fulfillmentQueue?.name === FulfillmentQueueName.Packing) {
                  // Order-level selection for Triage/PV1/PV2/Packing
                  return [{ _id: item._id, order: item }]
                } else return null
              })
              .filter(n => !!n)
            bulkSetSelections(selectMe as Selectable[])
          }
        }}
      />
    )
  }

  return <></>
}

export default QueueSelectedCheckbox
